import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/lib/auth-context';
import { taskAPI, employeeAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, ListTodo, Trash2, Clock, AlertTriangle, Tag, FileText, CheckCircle2, Activity, History, Play, Square, Timer, Lightbulb, LayoutGrid } from 'lucide-react';
import { KanbanBoard } from '@/components/KanbanBoard';

const KANBAN_COLUMNS = [
  { key: 'todo', label: 'To Do', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  { key: 'in_progress', label: 'In Progress', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'blocked', label: 'Blocked', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { key: 'done', label: 'Done', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
];

function KanbanTaskCard({ task, onView }) {
  return (
    <Card className="hover:shadow-sm transition-shadow" onClick={() => onView(task)}>
      <CardContent className="p-2.5 space-y-2">
        <p className="text-sm font-medium leading-tight line-clamp-2">{task.title}</p>
        <Progress value={task.progress} className="h-1" />
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Avatar className="h-4 w-4"><AvatarFallback className="text-[7px] bg-primary/10 text-primary">{initials(task.assignee_name)}</AvatarFallback></Avatar>
            {task.assignee_name}
          </span>
          {task.is_overdue
            ? <span className="flex items-center gap-0.5 text-destructive font-medium"><AlertTriangle size={10} /> Overdue</span>
            : task.expected_completion_date && <span>{task.expected_completion_date}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

const STATUS = {
  todo: { label: 'To Do', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  blocked: { label: 'Blocked', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  done: { label: 'Done', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const initials = (name) => (name || '?').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const fmtDuration = (sec) => {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const liveSeconds = (task, nowMs = Date.now()) => {
  let total = task.total_seconds || 0;
  if (task.timer_running && task.timer_started_at) {
    total += Math.max(0, (nowMs - new Date(task.timer_started_at).getTime()) / 1000);
  }
  return total;
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.todo;
  return <Badge variant="outline" className={s.cls} data-testid={`status-badge-${status}`}>{s.label}</Badge>;
}

function TaskCard({ task, onLog, onView, onDelete, onStatusChange, onStartTimer, onStopTimer, canEdit, nowMs }) {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`task-card-${task.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <button onClick={() => onView(task)} className="text-left flex-1 min-w-0" data-testid={`task-title-${task.id}`}>
            <p className="font-medium leading-tight truncate">{task.title}</p>
            {task.description && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{task.description}</p>}
          </button>
          <StatusBadge status={task.status} />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Progress</span>
            <span className="tabular-nums font-medium">{task.progress}%</span>
          </div>
          <Progress value={task.progress} className="h-1.5" />
        </div>

        {task.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {task.tags.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                <Tag size={10} /> {t}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3 text-muted-foreground">
            <span className="flex items-center gap-1"><Avatar className="h-4 w-4"><AvatarFallback className="text-[8px] bg-primary/10 text-primary">{initials(task.assignee_name)}</AvatarFallback></Avatar>{task.assignee_name}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {task.days_active}d active</span>
          </div>
          {task.is_overdue && (
            <span className="flex items-center gap-1 text-destructive font-medium" data-testid={`overdue-${task.id}`}>
              <AlertTriangle size={12} /> Overdue
            </span>
          )}
        </div>

        {task.expected_completion_date && (
          <p className="text-[11px] text-muted-foreground">Due: {task.expected_completion_date}</p>
        )}

        {/* Timer */}
        <div className="flex items-center justify-between gap-2 pt-2 border-t">
          <span className="flex items-center gap-1.5 text-xs font-medium tabular-nums" data-testid={`timer-display-${task.id}`}>
            <Timer size={13} className={task.timer_running ? 'text-emerald-600' : 'text-muted-foreground'} />
            {fmtDuration(liveSeconds(task, nowMs))}
            {task.timer_running && <span className="text-emerald-600 text-[10px] animate-pulse">● running</span>}
          </span>
          {canEdit && task.status !== 'done' && (
            task.timer_running ? (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs border-emerald-300 text-emerald-700" onClick={() => onStopTimer(task)} data-testid={`stop-timer-${task.id}`}>
                <Square size={11} /> Stop
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => onStartTimer(task)} data-testid={`start-timer-${task.id}`}>
                <Play size={11} /> Start
              </Button>
            )
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          {canEdit && task.status !== 'done' && (
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs flex-1" onClick={() => onLog(task)} data-testid={`log-update-btn-${task.id}`}>
              <Activity size={12} /> Log Update
            </Button>
          )}
          {canEdit && (
            <Select value={task.status} onValueChange={(v) => onStatusChange(task, v)}>
              <SelectTrigger className="h-7 w-[120px] text-xs" data-testid={`status-select-${task.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" data-testid={`delete-task-btn-${task.id}`}>
                  <Trash2 size={13} />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete task?</AlertDialogTitle>
                  <AlertDialogDescription>This will permanently delete {'"'}{task.title}{'"'} and all its updates.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(task)} data-testid={`confirm-delete-${task.id}`}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function TasksPage() {
  const { user, isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('mine');

  // dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', tags: '', assignee_id: '', expected_completion_date: '' });
  const [logTask, setLogTask] = useState(null);
  const [logForm, setLogForm] = useState({ date: '', summary: '', hours_spent: '', files_touched: '', progress_after: '' });
  const [detailTask, setDetailTask] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [officeHours, setOfficeHours] = useState(null);

  // Tick every second while any timer is running
  useEffect(() => {
    if (!tasks.some(t => t.timer_running)) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tasks]);

  useEffect(() => {
    if (user?.branch_id) branchAPI.todaySchedule(user.branch_id).then(r => setOfficeHours(r.data)).catch((e) => console.error(e));
  }, [user?.branch_id]);

  const loadTasks = useCallback(async () => {
    try {
      const res = await taskAPI.list();
      setTasks(res.data);
    } catch { toast.error('Failed to load tasks'); }
    finally { setLoading(false); }
  }, []);

  const loadActivity = useCallback(async () => {
    try { const res = await taskAPI.activity({ limit: 80 }); setActivity(res.data); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadTasks();
    if (isAdmin) {
      loadActivity();
      employeeAPI.list().then(r => setEmployees(r.data)).catch((e) => console.error(e));
    }
  }, [loadTasks, loadActivity, isAdmin]);

  const myTasks = useMemo(() => tasks.filter(t => t.assignee_id === user?.id || t.created_by === user?.id), [tasks, user]);

  // Productivity: tracked time vs ~60% of office hours
  const trackedSec = useMemo(() => myTasks.reduce((s, t) => s + liveSeconds(t, nowMs), 0), [myTasks, nowMs]);
  const officeSec = useMemo(() => {
    if (!officeHours || !officeHours.is_working || !officeHours.start_time || !officeHours.end_time) return 0;
    const [sh, sm] = officeHours.start_time.split(':').map(Number);
    const [eh, em] = officeHours.end_time.split(':').map(Number);
    return Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) * 60);
  }, [officeHours]);
  const targetSec = officeSec * 0.6;
  const trackingLow = officeSec > 0 && trackedSec < targetSec;

  const grouped = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const key = t.assignee_id;
      if (!map[key]) map[key] = { name: t.assignee_name, tasks: [] };
      map[key].tasks.push(t);
    });
    return Object.entries(map).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [tasks]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.expected_completion_date) { toast.error('Expected completion date is required'); return; }
    try {
      await taskAPI.create({
        title: form.title,
        description: form.description,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        assignee_id: form.assignee_id || undefined,
        expected_completion_date: form.expected_completion_date,
      });
      toast.success('Task created');
      setNewOpen(false);
      setForm({ title: '', description: '', tags: '', assignee_id: '', expected_completion_date: '' });
      loadTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create task'); }
  };

  const handleLog = async () => {
    if (!logForm.summary.trim()) { toast.error('Please describe what you did'); return; }
    try {
      await taskAPI.addLog(logTask.id, {
        date: logForm.date || undefined,
        summary: logForm.summary,
        hours_spent: parseFloat(logForm.hours_spent) || 0,
        files_touched: logForm.files_touched,
        progress_after: logForm.progress_after !== '' ? parseInt(logForm.progress_after, 10) : undefined,
      });
      toast.success('Update logged');
      setLogTask(null);
      setLogForm({ date: '', summary: '', hours_spent: '', files_touched: '', progress_after: '' });
      loadTasks();
      if (isAdmin) loadActivity();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to log update'); }
  };

  const handleStatusChange = async (task, status) => {
    try { await taskAPI.update(task.id, { status }); loadTasks(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const handleStartTimer = async (task) => {
    try { await taskAPI.startTimer(task.id); loadTasks(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to start timer'); }
  };

  const handleStopTimer = async (task) => {
    try { await taskAPI.stopTimer(task.id); loadTasks(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to stop timer'); }
  };


  const handleDelete = async (task) => {
    try { await taskAPI.delete(task.id); toast.success('Task deleted'); loadTasks(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const openLog = (task) => {
    setLogForm({ date: '', summary: '', hours_spent: '', files_touched: '', progress_after: String(task.progress) });
    setLogTask(task);
  };

  const openDetail = async (task) => {
    try { const res = await taskAPI.get(task.id); setDetailTask(res.data); }
    catch { toast.error('Failed to load task'); }
  };

  const canEditTask = (task) => isAdmin || task.assignee_id === user?.id || task.created_by === user?.id;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Task Management</h1>
          <p className="text-muted-foreground">Track what each member is working on and their progress</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="gap-2" data-testid="new-task-button">
          <Plus size={16} /> New Task
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="tasks-tabs">
          <TabsTrigger value="mine" data-testid="tab-mine"><ListTodo size={14} className="mr-1.5" /> My Tasks</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban"><LayoutGrid size={14} className="mr-1.5" /> Board</TabsTrigger>
          {isAdmin && <TabsTrigger value="board" data-testid="tab-board"><Activity size={14} className="mr-1.5" /> By Member</TabsTrigger>}
          {isAdmin && <TabsTrigger value="activity" data-testid="tab-activity"><History size={14} className="mr-1.5" /> Activity Feed</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine" className="mt-4">
          {officeSec > 0 && (
            <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 mb-4 text-sm ${trackingLow ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="productivity-nudge">
              <Lightbulb size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  Tracked today: {fmtDuration(trackedSec)} · target ~{fmtDuration(targetSec)} (60% of {fmtDuration(officeSec)} office hours)
                </p>
                {trackingLow ? (
                  <p className="text-xs mt-1 opacity-90">
                    Your tracked task time looks low. Break work into smaller, specific tasks and run the timer. E.g.,
                    <span className="font-medium"> {'"Draft SOP — Sharma PR (1.5h)"'}</span>,
                    <span className="font-medium"> {'"Client call + notes — Gupta file (0.5h)"'}</span>,
                    <span className="font-medium"> {'"Review docs checklist — Singh study visa (1h)"'}</span>.
                  </p>
                ) : (
                  <p className="text-xs mt-1 opacity-90">Great — your tracked time is on target. Keep logging granular tasks.</p>
                )}
              </div>
            </div>
          )}
          {loading ? <p className="text-muted-foreground py-8 text-center">Loading...</p> :
            myTasks.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <ListTodo size={32} className="mx-auto mb-2 opacity-40" />
                No tasks yet. Click {'"New Task"'} to add what you{'\u2019'}re working on.
              </CardContent></Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {myTasks.map(t => (
                  <TaskCard key={t.id} task={t} canEdit={canEditTask(t)}
                    onLog={openLog} onView={openDetail} onDelete={handleDelete} onStatusChange={handleStatusChange}
                    onStartTimer={handleStartTimer} onStopTimer={handleStopTimer} nowMs={nowMs} />
                ))}
              </div>
            )}
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          <p className="text-xs text-muted-foreground mb-3">Drag cards between columns to update status.</p>
          <KanbanBoard
            columns={KANBAN_COLUMNS}
            items={isAdmin ? tasks : myTasks}
            getStatus={(t) => t.status}
            getId={(t) => t.id}
            onMove={(id, status) => handleStatusChange({ id }, status)}
            renderCard={(t) => <KanbanTaskCard task={t} onView={openDetail} />}
          />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="board" className="mt-4 space-y-6">
            {grouped.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">No tasks assigned to your team yet.</CardContent></Card>
            ) : grouped.map(([uid, group]) => {
              const done = group.tasks.filter(t => t.status === 'done').length;
              return (
                <div key={uid} className="space-y-3" data-testid={`member-group-${uid}`}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{initials(group.name)}</AvatarFallback></Avatar>
                    <h3 className="font-medium">{group.name}</h3>
                    <Badge variant="secondary" className="text-xs">{group.tasks.length} tasks</Badge>
                    <Badge variant="outline" className="text-xs gap-1"><CheckCircle2 size={11} className="text-emerald-600" /> {done} done</Badge>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {group.tasks.map(t => (
                      <TaskCard key={t.id} task={t} canEdit={canEditTask(t)}
                        onLog={openLog} onView={openDetail} onDelete={handleDelete} onStatusChange={handleStatusChange}
                    onStartTimer={handleStartTimer} onStopTimer={handleStopTimer} nowMs={nowMs} />
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="activity" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-lg font-heading flex items-center gap-2"><History size={18} className="text-primary" /> Recent Updates</CardTitle></CardHeader>
              <CardContent>
                {activity.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No activity logged yet.</p>
                ) : (
                  <div className="space-y-4">
                    {activity.map(log => (
                      <div key={log.id} className="flex gap-3 pb-4 border-b last:border-0" data-testid={`activity-${log.id}`}>
                        <Avatar className="h-8 w-8 mt-0.5"><AvatarFallback className="text-[10px] bg-primary/10 text-primary">{initials(log.user_name)}</AvatarFallback></Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{log.user_name}</span>
                            <span className="text-xs text-muted-foreground">on</span>
                            <span className="text-sm font-medium text-primary truncate">{log.task_title}</span>
                            <span className="text-xs text-muted-foreground ml-auto">{log.date}</span>
                          </div>
                          <p className="text-sm text-foreground/80 mt-1">{log.summary}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                            {log.hours_spent > 0 && <span>{log.hours_spent}h</span>}
                            {log.files_touched && <span className="flex items-center gap-1"><FileText size={11} /> {log.files_touched}</span>}
                            <span>→ {log.progress_after}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* New Task Dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">New Task</DialogTitle>
            <DialogDescription>Add a task you{'\u2019'}re working on. Open tasks carry over until marked done.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Prepare Sharma PR application" data-testid="task-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Details / what needs to be done" rows={2} data-testid="task-desc-input" />
            </div>
            <div className="space-y-2">
              <Label>Tags / Case references <span className="text-muted-foreground text-xs">(comma separated)</span></Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="e.g., Sharma PR, Express Entry" data-testid="task-tags-input" />
            </div>
            {isAdmin && (
              <div className="space-y-2">
                <Label>Assign to</Label>
                <Select value={form.assignee_id || 'self'} onValueChange={v => setForm({ ...form, assignee_id: v === 'self' ? '' : v })}>
                  <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Myself" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself ({user?.name})</SelectItem>
                    {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Expected completion date</Label>
              <Input type="date" value={form.expected_completion_date} onChange={e => setForm({ ...form, expected_completion_date: e.target.value })} data-testid="task-due-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="task-save-button">Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Update Dialog */}
      <Dialog open={!!logTask} onOpenChange={(o) => !o && setLogTask(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Log Daily Update</DialogTitle>
            <DialogDescription>{logTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>What did you do?</Label>
              <Textarea value={logForm.summary} onChange={e => setLogForm({ ...logForm, summary: e.target.value })} placeholder="Describe today's progress" rows={3} data-testid="log-summary-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={logForm.date} onChange={e => setLogForm({ ...logForm, date: e.target.value })} data-testid="log-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Hours spent</Label>
                <Input type="number" step="0.5" min="0" value={logForm.hours_spent} onChange={e => setLogForm({ ...logForm, hours_spent: e.target.value })} placeholder="0" data-testid="log-hours-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Files / Cases touched</Label>
              <Input value={logForm.files_touched} onChange={e => setLogForm({ ...logForm, files_touched: e.target.value })} placeholder="e.g., Sharma case, intake form" data-testid="log-files-input" />
            </div>
            <div className="space-y-2">
              <Label>Progress now: <span className="font-medium tabular-nums">{logForm.progress_after || 0}%</span></Label>
              <Input type="range" min="0" max="100" step="5" value={logForm.progress_after || 0} onChange={e => setLogForm({ ...logForm, progress_after: e.target.value })} data-testid="log-progress-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogTask(null)}>Cancel</Button>
            <Button onClick={handleLog} data-testid="log-save-button">Save Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <Dialog open={!!detailTask} onOpenChange={(o) => !o && setDetailTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">{detailTask?.title} {detailTask && <StatusBadge status={detailTask.status} />}</DialogTitle>
            {detailTask?.description && <DialogDescription>{detailTask.description}</DialogDescription>}
          </DialogHeader>
          {detailTask && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Assignee:</span> {detailTask.assignee_name}</div>
                <div><span className="text-muted-foreground">Progress:</span> {detailTask.progress}%</div>
                <div><span className="text-muted-foreground">Active for:</span> {detailTask.days_active} days</div>
                <div><span className="text-muted-foreground">Due:</span> {detailTask.expected_completion_date || '—'} {detailTask.is_overdue && <span className="text-destructive">(overdue)</span>}</div>
              </div>
              {detailTask.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detailTask.tags.map((t, i) => <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground"><Tag size={10} /> {t}</span>)}
                </div>
              )}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><History size={14} /> Update History ({detailTask.logs?.length || 0})</p>
                {(!detailTask.logs || detailTask.logs.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No updates logged yet.</p>
                ) : (
                  <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                    {detailTask.logs.map(log => (
                      <div key={log.id} className="text-sm border-l-2 border-primary/30 pl-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-xs">{log.user_name}</span>
                          <span className="text-xs text-muted-foreground">{log.date} · {log.progress_after}%</span>
                        </div>
                        <p className="text-foreground/80 mt-0.5">{log.summary}</p>
                        {log.files_touched && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><FileText size={10} /> {log.files_touched}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
